import type { DocumentValue } from '../../components/DocumentUpload';
import { MSG, parseEditQty } from '../../components/pickingEdit';
import { fmtQty, norm } from '../../format';
import { parseWarehouseError } from '../../api/warehouse.api';
import type { FieldTarget } from '../../hooks/useServerErrors';
import {
  type ClientRef,
  type CreateStockOpnamePayload,
  type FieldError,
  type OpnameBatch,
  type OpnameImportLine,
  type StockOpnameLine,
  type StockOpnameSummary,
  type WarehouseStockType,
} from '../../types';

/**
 * Pure model of the "Buat stock opname" form: the draft rows (a counted lot,
 * or a batch the system does not know yet, + the physical qty typed for it),
 * their checks, the discrepancy totals, the POST body and the mapping of a
 * server error back onto the form. No React here — StockOpnameNewPage renders it.
 */

export type LineMode = 'existing' | 'new';

export interface DraftLine {
  /** Stable React key (rows can be removed from the middle). */
  key: number;
  /** `existing`: a lot the system holds; `new`: stock found that it did not have. */
  mode: LineMode;
  storageID: string;
  storageName: string;
  /** "Location / Section / Bin"; '' when only the name is known. */
  storagePath: string;
  sku: string;
  itemName: string;
  uomName: string;
  /** The counted lot's batch number; '' for a new batch (the server issues it). */
  batchNumber: string;
  /** `YYYY-MM-DD`; '' = none (an existing lot's, or a new batch's optional one). */
  expiredDate: string;
  /**
   * `existing`: the picked row's type (null while the server did not say —
   * then none is sent and the server counts the batch's only row); `new`: the
   * type the found stock is booked as, REGULAR until changed.
   */
  stockType: WarehouseStockType | null;
  /** On hand at the time the row was picked (0 for a new batch). */
  qtySystem: number;
  /** Raw input ("12", "1,5"). */
  physical: string;
  notes: string;
}

let seq = 0;

export const newLine = (mode: LineMode = 'existing'): DraftLine => ({
  key: ++seq,
  mode,
  storageID: '',
  storageName: '',
  storagePath: '',
  sku: '',
  itemName: '',
  uomName: '',
  batchNumber: '',
  expiredDate: '',
  stockType: mode === 'new' ? 'REGULAR' : null,
  qtySystem: 0,
  physical: '',
  notes: '',
});

/** The row's counted lot is chosen (existing) / its SKU and storage are (new). */
export const isPicked = (l: DraftLine): boolean =>
  l.mode === 'existing' ? !!l.sku && !!l.batchNumber : !!l.sku && !!l.storageID;

/**
 * Identity of a counted lot — what a picker excludes once a row holds it. The
 * same batch as REGULAR and RETURN is two lots (two stock rows).
 */
export const lotKey = (
  storageID: string,
  sku: string,
  batch: string,
  stockType: WarehouseStockType | null
): string => `${storageID}|${sku}|${batch}|${stockType ?? ''}`;

/** The row's lot key; '' while no lot is chosen (a new batch has none). */
export const lineLotKey = (l: DraftLine): string =>
  l.mode === 'existing' && isPicked(l)
    ? lotKey(l.storageID, l.sku, l.batchNumber, l.stockType)
    : '';

/** A lot picked from the batch list. */
export const lineFromBatch = (line: DraftLine, b: OpnameBatch): DraftLine => ({
  ...line,
  mode: 'existing',
  storageID: b.storageID,
  storageName: b.storageName,
  storagePath: b.storagePath,
  sku: b.sku,
  itemName: b.itemName,
  uomName: b.uomName,
  batchNumber: b.batchNumber,
  expiredDate: b.expiredDate ?? '',
  stockType: b.stockType ?? null,
  qtySystem: b.qtyOnHand,
  physical: '',
});

/** A resolved row of the CSV dry run. */
export const lineFromImport = (l: OpnameImportLine): DraftLine => ({
  ...newLine(l.batchNumber ? 'existing' : 'new'),
  storageID: l.storageID,
  storageName: l.storageName,
  storagePath: '',
  sku: l.sku,
  itemName: l.itemName,
  uomName: l.uomName,
  batchNumber: l.batchNumber,
  expiredDate: l.expiredDate ?? '',
  stockType: l.stockType,
  qtySystem: l.qtySystem,
  physical: String(l.qtyPhysical),
  notes: l.notes,
});

export const OPNAME_MSG = {
  client: 'Client wajib dipilih.',
  report: 'Berita acara (PDF) wajib diunggah.',
  pickBatch: 'Pilih produk & batch.',
  pickSku: 'Pilih SKU.',
  pickStorage: 'Pilih storage.',
  physicalRequired: 'Qty fisik wajib diisi.',
  physicalInvalid: 'Qty fisik harus berupa angka 0 atau lebih.',
  newPositive: 'Batch baru harus punya qty fisik lebih dari 0.',
  noLines: 'Tambahkan minimal satu produk yang dihitung.',
  duplicate: (row: number) => `Batch ini sudah dihitung di baris ${row}.`,
} as const;

/** Physical qty of a valid row; null while it is empty or invalid. */
export const physicalOf = (l: DraftLine): number | null =>
  parseEditQty(l.physical);

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** physical − system of a row; null while the physical qty is not valid. */
export const gapOf = (l: DraftLine): number | null => {
  const p = physicalOf(l);
  return p === null ? null : round2(p - l.qtySystem);
};

/** The problem with a typed physical qty (nothing for an empty one), or null. */
export const physicalIssue = (l: DraftLine): string | null => {
  const raw = l.physical.trim().replace(',', '.');
  if (!raw) return null;
  const qty = parseEditQty(raw);
  if (qty === null) {
    return /^\d*\.\d{3,}$/.test(raw)
      ? MSG.qtyDecimals
      : OPNAME_MSG.physicalInvalid;
  }
  if (l.mode === 'new' && qty <= 0) return OPNAME_MSG.newPositive;
  return null;
};

/** What makes two rows count the same stock (a new batch: same bin, SKU, expiry, type). */
const dupKey = (l: DraftLine): string =>
  l.mode === 'existing'
    ? lineLotKey(l)
    : isPicked(l)
      ? `new|${l.storageID}|${l.sku}|${l.expiredDate}|${l.stockType ?? ''}`
      : '';

/** Problems of step 2, each pointing at its row (`lines[i].…`). */
export const checkLines = (lines: readonly DraftLine[]): FieldError[] => {
  if (!lines.length) return [{ field: 'lines', message: OPNAME_MSG.noLines }];
  const out: FieldError[] = [];
  const seen = new Map<string, number>();
  lines.forEach((l, i) => {
    const add = (field: string, message: string) =>
      out.push({ field: `lines[${i}].${field}`, message, lineIndex: i });
    if (l.mode === 'existing' && !isPicked(l))
      return add('batchNumber', OPNAME_MSG.pickBatch);
    if (l.mode === 'new' && !l.sku) return add('sku', OPNAME_MSG.pickSku);
    if (l.mode === 'new' && !l.storageID)
      return add('storageID', OPNAME_MSG.pickStorage);
    const key = dupKey(l);
    const first = key ? seen.get(key) : undefined;
    if (first !== undefined)
      return add('batchNumber', OPNAME_MSG.duplicate(first + 1));
    if (key) seen.set(key, i);
    if (!l.physical.trim())
      return add('qtyPhysical', OPNAME_MSG.physicalRequired);
    const issue = physicalIssue(l);
    if (issue) return add('qtyPhysical', issue);
    return undefined;
  });
  return out;
};

/** Problems of step 1. */
export const checkInfo = (
  client: ClientRef | null,
  report: DocumentValue | null
): FieldError[] => {
  const out: FieldError[] = [];
  if (!client) out.push({ field: 'clientID', message: OPNAME_MSG.client });
  if (!report) out.push({ field: 'reportUrl', message: OPNAME_MSG.report });
  return out;
};

/* ------------------------------ discrepancy ------------------------------ */

export type OpnameTotals = Pick<
  StockOpnameSummary,
  | 'lineCount'
  | 'gapLineCount'
  | 'totalQtySystem'
  | 'totalQtyPhysical'
  | 'totalGap'
>;

/** Totals over the rows that are picked and have a valid physical qty. */
export const summarize = (lines: readonly DraftLine[]): OpnameTotals => {
  const counted = lines.filter((l) => isPicked(l) && physicalOf(l) !== null);
  const system = counted.reduce((t, l) => t + l.qtySystem, 0);
  const physical = counted.reduce((t, l) => t + (physicalOf(l) ?? 0), 0);
  return {
    lineCount: counted.length,
    gapLineCount: counted.filter((l) => gapOf(l) !== 0).length,
    totalQtySystem: round2(system),
    totalQtyPhysical: round2(physical),
    totalGap: round2(counted.reduce((t, l) => t + (gapOf(l) ?? 0), 0)),
  };
};

/** Signed gap: "+5", "-3", "0". */
export const fmtGap = (n: number): string =>
  n > 0 ? `+${fmtQty(n)}` : fmtQty(n);

/** Gap colour: error below zero, success above, neutral at zero. */
export const gapCls = (n: number): string =>
  n < 0
    ? 'text-(--state-error-base)'
    : n > 0
      ? 'text-(--state-success-base)'
      : 'text-text-sub-600';

/** Share of counted rows whose count differs, in whole percent. */
export const gapPercent = (
  t: Pick<OpnameTotals, 'lineCount' | 'gapLineCount'>
) => (t.lineCount ? Math.round((t.gapLineCount / t.lineCount) * 100) : 0);

/* -------------------------------- payload -------------------------------- */

export const toPayload = (form: {
  hubID: number;
  hubName?: string;
  client: ClientRef;
  notes: string;
  reportUrl: string;
  lines: readonly DraftLine[];
}): CreateStockOpnamePayload => ({
  hubID: form.hubID,
  ...(form.hubName ? { hubName: form.hubName } : {}),
  clientID: form.client.id,
  clientName: form.client.name,
  ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
  reportUrl: form.reportUrl,
  lines: form.lines.map((l) => ({
    storageID: l.storageID,
    sku: l.sku,
    ...(l.mode === 'existing'
      ? { batchNumber: l.batchNumber }
      : l.expiredDate
        ? { expiredDate: l.expiredDate }
        : {}),
    ...(l.stockType ? { stockType: l.stockType } : {}),
    qtyPhysical: physicalOf(l) ?? Number(l.physical),
    ...(l.notes.trim() ? { notes: l.notes.trim() } : {}),
  })),
});

/** The review table's rows — same shape as a saved opname line. */
export const reviewLines = (lines: readonly DraftLine[]): StockOpnameLine[] =>
  lines.map((l, i) => ({
    lineNo: i + 1,
    storageID: l.storageID,
    storageName: l.storageName,
    storagePath: l.storagePath,
    stockID: null,
    sku: l.sku,
    itemName: l.itemName,
    uomName: l.uomName,
    batchNumber: l.batchNumber,
    expiredDate: l.expiredDate || null,
    stockType: l.stockType ?? 'REGULAR',
    isNewBatch: l.mode === 'new',
    qtySystem: l.qtySystem,
    qtyPhysical: physicalOf(l) ?? 0,
    qtyGap: gapOf(l) ?? 0,
    notes: l.notes.trim(),
  }));

/* ---------------------------- server errors ------------------------------ */

export type OpnameFieldKey = 'client' | 'report' | 'notes' | 'lines' | 'form';

const STEP_OF: Record<OpnameFieldKey, number> = {
  client: 1,
  report: 1,
  notes: 1,
  lines: 2,
  form: 3,
};
export const opnameStepOf = (k: OpnameFieldKey): number => STEP_OF[k];

/** The line row an error names (`lineIndex`, else `lines[2].…`), or null. */
const lineOf = (e: FieldError): number | null => {
  if (e.lineIndex !== undefined && e.lineIndex !== null) return e.lineIndex;
  const m = /lines?\W*(\d+)/i.exec(e.field || '');
  return m ? Number(m[1]) : null;
};

/** Map a backend field (snake, dotted or camel) onto an opname form field. */
export const opnameFieldOf = (e: FieldError): FieldTarget<OpnameFieldKey> => {
  const n = norm(e.field || '');
  if (!n) return { key: 'form' };
  const line = lineOf(e);
  if (
    line !== null ||
    n.startsWith('line') ||
    [
      'sku',
      'storageid',
      'batchnumber',
      'qtyphysical',
      'expireddate',
      'stocktype',
    ].includes(n)
  )
    return { key: 'lines', line };
  if (n.includes('client')) return { key: 'client' };
  if (n.includes('report')) return { key: 'report' };
  if (n === 'notes') return { key: 'notes' };
  return { key: 'form' };
};

/** Machine codes the server answers with that belong to one field. */
export const OPNAME_CODE_FIELD: Readonly<Record<string, string>> = {
  SKU_INACTIVE: 'lines',
};

/** One row the server refused: its (storage, SKU) is in another open opname. */
export interface LockedRow {
  lineIndex: number | null;
  sku: string;
  /** Code of the opname that holds it. */
  opnameCode: string;
}

/** The `OPNAME_LOCKED` rows of a failed create (empty for any other error). */
export const lockedRowsOf = (err: unknown): LockedRow[] => {
  const e = parseWarehouseError(err);
  if (e.code !== 'OPNAME_LOCKED') return [];
  return e.errors.flatMap((d) => {
    const r = d as {
      rule?: unknown;
      lineIndex?: unknown;
      sku?: unknown;
      opnameCode?: unknown;
    } | null;
    if (!r || typeof r !== 'object' || r.rule !== 'OPNAME_LOCKED') return [];
    return [
      {
        lineIndex: typeof r.lineIndex === 'number' ? r.lineIndex : null,
        sku: typeof r.sku === 'string' ? r.sku : '',
        opnameCode: typeof r.opnameCode === 'string' ? r.opnameCode : '',
      },
    ];
  });
};
